import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import { formatItemPriceEgp } from '../../utils/formatPrice';

const INSTAPAY_PENDING_KEY = 'flea_instapay_pending_payment';
const DEFAULT_QR_URL = 'https://ipn.eg/S/ranakhamiss/instapay/5IYZ56';
const DEFAULT_WHATSAPP_NUMBER = '+201040895962';

const Page = styled.div`
  max-width: 760px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const Card = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.9rem;
  padding: 1.25rem;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.06);
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
`;

const Highlight = styled.div`
  margin: 0.75rem 0 1rem;
  padding: 0.9rem;
  border-radius: 0.6rem;
  background: #f0fdf4;
  border: 1px solid #86efac;
  color: #14532d;
`;

const Steps = styled.ol`
  margin: 0.5rem 0 0;
  padding-left: 1.2rem;
  line-height: 1.7;
`;

const Actions = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
`;

const Btn = styled.button`
  border: none;
  border-radius: 0.55rem;
  padding: 0.7rem 1rem;
  font-weight: 700;
  cursor: pointer;
  background: var(--primary-color);
  color: white;
`;

const LinkBtn = styled.a`
  border-radius: 0.55rem;
  padding: 0.7rem 1rem;
  font-weight: 700;
  text-decoration: none;
  background: #111827;
  color: #fff;
`;

const QrBlock = styled.div`
  margin: 1rem 0;
  padding: 1rem;
  border-radius: 0.75rem;
  border: 1px solid var(--border-color);
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
`;

const QrImage = styled.img`
  width: min(320px, 100%);
  height: auto;
  border-radius: 0.5rem;
  background: #fff;
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
`;

const QrHint = styled.p`
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
  text-align: center;
`;

export default function InstapayInstructionsPage() {
  const location = useLocation();
  const navigate = useNavigate();

  const details = useMemo(() => {
    const fromState = location.state && typeof location.state === 'object' ? location.state : null;
    if (fromState) return fromState;
    try {
      const cached = sessionStorage.getItem(INSTAPAY_PENDING_KEY);
      return cached ? JSON.parse(cached) : null;
    } catch (_e) {
      return null;
    }
  }, [location.state]);

  const qrUrl = details?.qrUrl || DEFAULT_QR_URL;
  const qrImageUrl = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=640x640&data=${encodeURIComponent(qrUrl)}`,
    [qrUrl]
  );
  const whatsappNumber = details?.supportWhatsApp || DEFAULT_WHATSAPP_NUMBER;
  const whatsappLink = `https://wa.me/${String(whatsappNumber).replace(/\D/g, '')}`;
  const amountDue = Number(details?.amountDueEgp || 0);
  const walletApplied = Number(details?.walletAppliedEgp || 0);
  const orderReference = details?.orderReference || `#${details?.intentId ?? 'N/A'}`;

  return (
    <Page>
      <Card>
        <Title>Instapay Payment Instructions</Title>
        <p>
          Your order is now <strong>pending manual Instapay verification</strong>. Buyer and seller balances
          stay on hold until admin confirms payment.
        </p>
        <Highlight>
          <div><strong>Order number:</strong> {orderReference}</div>
          <div><strong>Amount to send:</strong> {formatItemPriceEgp(amountDue)}</div>
          {walletApplied > 0 ? (
            <div><strong>Covered by wallet:</strong> {formatItemPriceEgp(walletApplied)}</div>
          ) : null}
        </Highlight>
        <QrBlock>
          <QrImage src={qrImageUrl} alt="Instapay payment QR code" />
          <QrHint>Scan this QR directly from your bank or Instapay app.</QrHint>
        </QrBlock>
        <Steps>
          <li>Send the exact amount via Instapay using the QR/payment link.</li>
          <li>Take a payment screenshot after transfer.</li>
          <li>Send the screenshot with your order number to <strong>(+20)1040895962</strong> on WhatsApp.</li>
          <li>Admin will review and approve the payment from dashboard, then order settlement proceeds.</li>
        </Steps>
        <Actions>
          <LinkBtn href={qrUrl} target="_blank" rel="noopener noreferrer">Open Instapay Payment Link</LinkBtn>
          <LinkBtn href={whatsappLink} target="_blank" rel="noopener noreferrer">Open WhatsApp</LinkBtn>
          <Btn type="button" onClick={() => navigate('/orders')}>Go to My Orders</Btn>
        </Actions>
      </Card>
    </Page>
  );
}
